import { ArrowUpDown, Check, RotateCcw } from "lucide-react";
import { T } from "../lib/theme";
import { SORTS, activeFilterCount, type Filters, type SortKey, type SpokenFilter } from "../lib/filters";
import { Sheet, SheetHeader } from "./ui";

/** Spelled out rather than "All / Only / Hide" — this decides who disappears
 *  from the list, so it should read as a sentence, not a toggle. */
const SPOKEN: Array<{ v: SpokenFilter; label: string; hint: string }> = [
  { v: "all", label: "Everyone", hint: "No filtering on whether they've spoken." },
  { v: "only-never", label: "Only those who have never spoken", hint: "The people most overdue to be asked." },
  { v: "hide-never", label: "Hide those who have never spoken", hint: "Only people with a talk on record." },
];

export function FiltersSheet({
  filters,
  set,
  reset,
  recentMonths,
  shown,
  total,
  onClose,
}: {
  filters: Filters;
  set: <K extends keyof Filters>(key: K, value: Filters[K]) => void;
  reset: () => void;
  recentMonths: number;
  shown: number;
  total: number;
  onClose: () => void;
}) {
  const active = activeFilterCount(filters);

  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: 20 }}>
        <SheetHeader title="Filters & sort" onClose={onClose} />

        <div style={{ fontSize: 12.5, color: T.muted, marginTop: -6, marginBottom: 18 }}>
          Showing <strong style={{ color: T.ink }}>{shown}</strong> of {total} in the ward.
        </div>

        <Group label="Include">
          <div style={{ display: "flex", gap: 8 }}>
            <Toggle on={filters.adults} onClick={() => set("adults", !filters.adults)}>
              Adults
            </Toggle>
            <Toggle on={filters.youth} onClick={() => set("youth", !filters.youth)}>
              Youth
            </Toggle>
          </div>
          {!filters.adults && !filters.youth && (
            <Hint tone="brass">Both are off, so the list will be empty.</Hint>
          )}
        </Group>

        <Group label="Who have spoken">
          {SPOKEN.map((o) => (
            <Row
              key={o.v}
              selected={filters.spoken === o.v}
              label={o.label}
              hint={o.hint}
              onClick={() => set("spoken", o.v)}
            />
          ))}
        </Group>

        <Group label="Recently spoken">
          <Row
            selected={filters.hideRecent}
            label={`Hide anyone who spoke in the last ${recentMonths} months`}
            hint="Change the window under Settings."
            onClick={() => set("hideRecent", !filters.hideRecent)}
          />
        </Group>

        {/* Full-bleed band: filters decide who appears, sort only reorders.
            A plain rule between them was too quiet to signal that. */}
        <div style={{ height: 8, background: T.paper, margin: "0 -20px 20px" }} />

        <Group label="Sort by" icon={<ArrowUpDown size={13} />}>
          {SORTS.map((o) => (
            <Row
              key={o.v}
              selected={filters.sort === o.v}
              label={o.label}
              onClick={() => set("sort", o.v as SortKey)}
            />
          ))}
        </Group>

        <button
          onClick={reset}
          disabled={active === 0 && filters.sort === "due"}
          style={{
            width: "100%",
            marginTop: 6,
            padding: 13,
            borderRadius: 11,
            fontSize: 14.5,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            border: `1px solid ${T.line}`,
            color: active || filters.sort !== "due" ? T.navy : T.faint,
          }}
        >
          <RotateCcw size={15} /> Reset to defaults
        </button>
      </div>
    </Sheet>
  );
}

function Group({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="eyebrow" style={{ marginBottom: 9, display: "flex", alignItems: "center", gap: 6 }}>
        {icon}
        {label}
      </div>
      {children}
    </div>
  );
}

/** A tap target that reads as a choice, with the check on the right the way the
 *  sort sheet already did it. */
function Row({
  selected,
  label,
  hint,
  onClick,
}: {
  selected: boolean;
  label: string;
  hint?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "11px 2px",
        borderBottom: `1px solid ${T.line}`,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
      }}
    >
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: 14.5, fontWeight: selected ? 600 : 400, color: selected ? T.navy : T.ink }}>
          {label}
        </span>
        {hint && (
          <span style={{ display: "block", fontSize: 11.5, color: T.faint, marginTop: 2, lineHeight: 1.4 }}>
            {hint}
          </span>
        )}
      </span>
      {selected && <Check size={18} style={{ color: T.accent, flexShrink: 0 }} />}
    </button>
  );
}

function Toggle({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      style={{
        flex: 1,
        padding: "10px 0",
        borderRadius: 9,
        fontSize: 14,
        fontWeight: 600,
        border: `1px solid ${on ? T.navy : T.line}`,
        background: on ? T.navy : "#fff",
        color: on ? "#fff" : T.faint,
      }}
    >
      {children}
    </button>
  );
}

function Hint({ children, tone }: { children: React.ReactNode; tone?: "brass" }) {
  return (
    <div
      style={{
        marginTop: 9,
        fontSize: 12,
        lineHeight: 1.45,
        color: tone === "brass" ? T.brass : T.muted,
        background: tone === "brass" ? T.brassSoft : T.paper,
        padding: "8px 10px",
        borderRadius: 7,
      }}
    >
      {children}
    </div>
  );
}
